import type { Department, Ending, Ent, Finding, Flag, LapView, Level, LogEntry, PipCfg, Wait } from "./types";

/** Engine-owned session fields. Levels add their own via `Level.init()`. */
export interface SessionBase {
  /** A level is loaded (false on the title screen). */
  live: boolean;
  /** Bumped on every start/exit so stale async work can detect it. */
  epoch: number;
  started: boolean;
  ended: boolean;
  finishing: boolean;
  busy: boolean;
  canEnd: boolean;
  /** Game minutes since level start. */
  t: number;
  log: LogEntry[];
  dialog: string | null;
  llmOff: boolean;
  findings: Finding[];
  ents: Ent[];
  flags: Record<string, Flag>;
  waits: Wait[];
  /** Per-character impatience counter. */
  mood: Record<string, number>;
  pip: PipCfg | null;
  pipText: string | null;
  speaking: string | null;
  cine: boolean;
  ending: Ending | null;
  /** performance.now() until which the idle clock is frozen (event banners). */
  pauseUntil: number;
  lap: LapView | null;
}

/**
 * The single game session. It is one stable object that gets wiped and
 * refilled per level, so level modules can hold a typed alias to it:
 *
 *   const S = session<MyLevelVars>();
 */
export const S = {} as SessionBase;

/** The active level. Only meaningful while `S.live`. */
export let L: Level;

/** The department the active level belongs to. Only meaningful while `S.live`. */
export let dept: Department;

/** Transient runtime flags that aren't part of the saved session. */
export const rt = {
  /** Skip the typewriter effect (set by clicking the log). */
  fastForward: false,
  /** Real seconds since the last game-minute tick. */
  idle: 0,
  /** Multiplier on cutscene pauses (e2e tests shrink it). */
  cineScale: 1,
};

let epochCounter = 0;

function wipe(): void {
  for (const k of Object.keys(S)) Reflect.deleteProperty(S, k);
}

function baseSession(live: boolean): SessionBase {
  return {
    live,
    epoch: ++epochCounter,
    started: false,
    ended: false,
    finishing: false,
    busy: false,
    canEnd: false,
    t: 0,
    log: [],
    dialog: null,
    llmOff: false,
    findings: [],
    ents: [],
    flags: {},
    waits: [],
    mood: {},
    pip: null,
    pipText: null,
    speaking: null,
    cine: false,
    ending: null,
    pauseUntil: 0,
    lap: null,
  };
}

export function beginSession(department: Department, level: Level): void {
  dept = department;
  L = level;
  level.reset?.();
  wipe();
  Object.assign(S, baseSession(true), level.init());
  S.ents = level.ents();
  rt.idle = 0;
  rt.fastForward = false;
}

export function endSession(): void {
  wipe();
  Object.assign(S, baseSession(false));
  rt.idle = 0;
  rt.fastForward = false;
}

/** Typed view of the session for a level module. */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- the level declares its own fields
export function session<V extends object>(): SessionBase & V {
  return S as SessionBase & V;
}

/**
 * Capture the current session; the returned check turns false once the
 * player restarts or leaves the level. Use around every await / timeout.
 */
export function sessionGuard(): () => boolean {
  const ep = S.epoch;
  return () => S.live && S.epoch === ep;
}

endSession();
